import React, { useContext, useEffect, useState } from 'react';
import { To, createSearchParams, useNavigate } from 'react-router-dom';
import {
	ButtonState,
	checkPayOneStatus,
	completeOrder,
	IAdditionalInformation,
	IOfferContext,
	ISepaInfo,
	ITypeSepaMandatFields,
	IVignetteContext,
	OfferContext,
	PayOneService,
	Variants,
	VignetteContext,
	getLabelValue,
	OrderService,
	getUrlBasePath,
} from '@adac-mp/shared';
import { Button, Column, Drawer, Modal, Row, Typography } from '../../index';
import { gatherProductTrackingData } from '../../utils/tracking/tracking';

interface ISepaDrawerProps {
	currentLabels?: ITypeSepaMandatFields;
	isOpen: boolean;
	paymentUpdate?: boolean;
	showExistingSepaModal: boolean;
	sepaText: string;
	iban: ISepaInfo;
	mandate: string;
	variant: Variants;
	additionalData?: IAdditionalInformation;
	setIsOpen: (bool: boolean) => void;
	setShowExistingSepaModal: (bool: boolean) => void;
	onOrderCompleted: (path: To) => void;
}

export const SepaDrawer = ({
	currentLabels = undefined,
	isOpen,
	paymentUpdate = false,
	sepaText,
	iban,
	mandate,
	variant,
	showExistingSepaModal,
	additionalData,
	setIsOpen,
	setShowExistingSepaModal,
	onOrderCompleted,
}: ISepaDrawerProps) => {
	const navigate = useNavigate();
	const { lang, labels, updateOrderInfo } = useContext<IOfferContext>(OfferContext);
	const { cartItems } = useContext<IVignetteContext>(VignetteContext);
	const [buttonState, setButtonState] = useState<ButtonState>('active');
	const [error, setError] = useState<string | undefined>(undefined);

	useEffect(() => {
		if (isOpen || showExistingSepaModal) {
			const offers = cartItems.map((item) => item.offer);
			gatherProductTrackingData({ offers, status: 'checkout', additionalData: { checkOutStep: 2 } });
		}
	}, [isOpen, showExistingSepaModal]);

	const sepaPreAuth = (customerHash, offerHash, cartHash) => {
		const token = sessionStorage.getItem('cartToken') || sessionStorage.getItem('customerToken');
		PayOneService.sepaPreauthorization({ customerHash, offerHash, cartHash, mandate, ...iban }, token, lang).then(
			(res) => {
				const { status, offerHash: preAuthOfferHash } = res;
				if (preAuthOfferHash) {
					sessionStorage.setItem('offerHash', preAuthOfferHash);
				}
				if (status === 'APPROVED' && customerHash) {
					checkPayOneStatus(preAuthOfferHash, cartHash, token, 3000, lang)
						.then((result) => {
							if (result) {
								completeOrder({
									lang,
									offerHash: result.offerHash,
									additionalData,
									variant,
									errorLabel: getLabelValue('mp.generalError', labels),
									updateOrderInfo,
									onOrderCompleted,
									gatherProductTrackingData,
									setButtonState,
								});
							}
						})
						.catch(() => {
							setButtonState('active');
							setError('validation took too long');
						});
				} else if (status === 'ERROR') {
					setButtonState('active');
					setError(res.errors[0]?.customerMessage || res.payoneError?.CustomerMessage);
				}
			},
		);
	};
	const sepaAmendPreAuth = (customerHash, offerHash, orderHash) => {
		const token = sessionStorage.getItem('token');
		PayOneService.amendSepaPreauthorization(
			{ customerHash, offerHash, orderHash, mandate, ...iban },
			token,
			lang,
		).then((res) => {
			const { status, offerHash: resOfferHash } = res;
			if (resOfferHash) {
				sessionStorage.setItem('offerHash', resOfferHash);
			}
			if (status === 'APPROVED' && customerHash && resOfferHash) {
				OrderService.amendOrderPayment({ orderHash, offerHash: resOfferHash, customerHash }, token).then(
					({ code, orderInfo }) => {
						if (code === 200 && orderInfo) {
							sessionStorage.setItem('orderInfo', JSON.stringify(orderInfo));
							const params = createSearchParams();
							params.set('type', 'updatepayment');
							navigate(getUrlBasePath('/order-confirmation', params));
						}
					},
				);
			} else if (status === 'ERROR') {
				setButtonState('active');
				setError(res.errors[0]?.customerMessage || res.payoneError?.CustomerMessage);
			}
		});
	};

	const completeSepaPayment = () => {
		const customerHash = sessionStorage.getItem('customerHash');
		const offerHash = sessionStorage.getItem('offerHash');
		const cartHash = sessionStorage.getItem('cartHash');
		const orderHash = sessionStorage.getItem('orderHash');

		setButtonState('loading');
		if (customerHash && (cartHash || offerHash || orderHash) && iban && mandate && buttonState === 'active') {
			return paymentUpdate
				? sepaAmendPreAuth(customerHash, offerHash, orderHash)
				: sepaPreAuth(customerHash, offerHash, cartHash);
		}
	};

	const closeModalHandler = () => {
		if (buttonState !== 'loading') {
			setShowExistingSepaModal(false);
		}
	};

	return (
		<>
			<Drawer
				isOpen={isOpen}
				setIsOpen={setIsOpen}
				title={currentLabels?.headlineSepa || 'SEPA Lastschriftsmandat'}
				paddingX="none"
				paddingBottom={false}
				variant="vertical">
				<Row className="pt-5 md:pb-10 md:pt-8 max-w-desktop justify-self-center md:w-1/2 px-1.5 md:px-0">
					<Column>
						<div dangerouslySetInnerHTML={{ __html: sepaText }} />

						{error && (
							<Typography size="lg" className="text-error block mt-6 mb-1.5">
								{error}
							</Typography>
						)}
					</Column>
				</Row>
				<div className="w-full left-0 sticky bottom-0 bg-white border-solid border-l-0 border-b-0 border-r-0 border-t border-gray-700 px-1.5 md:flex md:flex-col md:items-center">
					<Button
						fullWidth
						size="lg"
						data-cy="button: Zahlungspflichtig bestellen"
						loading={buttonState === 'loading'}
						className="my-1.5 md:w-1/2"
						tracking={{
							funnel_cta: {
								label: `${
									variant === 'dkv'
										? 'Zahlungsauftrag* und SEPA Mandat erteilen'
										: 'SEPA-Lastschriftmandat an ADAC Medien und Reise GmbH erteilen'
								}`,
								element: 'Checkout',
							},
						}}
						onClick={completeSepaPayment}>
						{variant.includes('dkv')
							? 'Zahlungsauftrag* und SEPA Mandat erteilen'
							: currentLabels?.sepaConfirmation ||
							  'SEPA-Lastschriftmandat an ADAC Medien und Reise GmbH erteilen'}
						{/* should be defined for other vignette flows */}
					</Button>
					{variant.includes('dkv') && (
						<Typography size="lg" className="pb-1.5">
							*Zahlungsauftrag an die AMR gem. Teil B. 3. der AGB der AMR
						</Typography>
					)}
				</div>
			</Drawer>
			<Modal
				isOpen={showExistingSepaModal}
				title={currentLabels?.headlineSepa || 'SEPA – Lastschriftmandat'}
				onClose={closeModalHandler}>
				<div className="px-3 pb-5">
					<Typography component="p" size="lg">
						{currentLabels?.existingSepaDescription ||
							'Für die eingegebene IBAN existiert bereits ein gültiges SEPA Lastschriftmandat. Ihre Bestellung wird über das vorhandene SEPA Lastschriftmandat abgebucht.'}
					</Typography>
					<Typography variant="title" component="p" size="lg" className="mt-5">
						{currentLabels?.existingMandatReferenceHeadline || 'Mandatsreferenz'}
					</Typography>
					<Typography size="lg">{mandate}</Typography>
				</div>
				<Button
					fullWidth
					data-cy="button: Zahlungspflichtig bestellen"
					loading={buttonState === 'loading'}
					tracking={{
						funnel_cta: {
							label: 'Zahlungspflichtig bestellen',
							element: 'Checkout',
						},
					}}
					onClick={completeSepaPayment}>
					{getLabelValue('mp.confirmPaymentButton', labels) || 'Zahlungspflichtig bestellen'}
				</Button>
			</Modal>
		</>
	);
};
